import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getSiteSuccessType, Offer } from '../../../APIDocs/apiTypes';

export type SiteDetails = {
  isWCJob: boolean;
  userMpid: string;
  offerDateRange: {
    startDate?: string;
    endDate?: string;
  };
  bookable?: boolean;
  siteAddress: StateAddressDetails;
  siteContactDetails: StateContactDetails;
  siteType: string;
  updatedSiteType?: string;
  bookingReason?: number;
};

export type SelectedOffer = {
  scheduledDate: string;
  offerToken: string;
  spJobCode: string;
  dayOfWeek: string;
  apptBand: string;
  apptDuration: string;
  startTime: string;
  endTime: string;
};

export type OfferDetails = {
  windowBookingStartDate?: string;
  offerTokenSelected: boolean;
  forceOfferSelection: boolean;
  selectedOffer?: SelectedOffer;
};

export enum EServiceLine {
  HH = 'HH',
  NHH = 'NHH',
  SUBMETER = 'SUBMETER',
  DNO = 'DNO',
  BNO = 'BNO',
  MSS = 'MSS',
  MSA = 'MSA'
}
export const serviceLinesRequiringContractId = [EServiceLine.NHH, EServiceLine.MSS, EServiceLine.MSA];

export type StateContactDetails = {
  title: string;
  contactFirstName: string;
  contactSurname: string;
  emailAddress: string;
  contactNumber: string;
  secondaryContactNumber?: string;
};

export type StateAddressDetails = {
  addressName: string;
  addressLine1: string;
  addressLine2?: string;
  addressLine3?: string;
  town: string;
  county: string;
  postCode: string;
};

export type DnoBnoSiteInfo = {
  exportCapacity: string;
  importCapacity: string;
  serviceVoltage: string;
  meterPointVoltage: string;
  hvMetering: string;
  ctRatio: string;
  vtRatio: string;
  serviceRating: string;
  fuseSize: string;
  customerOwnedSwitch: string;
  poNumber: string;
};

// Type containing every possible option for booking details. Workflow forms will not use every field
export type JobDetails = {
  jobTypeCode: string;
  parkingAvailable: boolean;
  siteInductionRequired: boolean;
  liftOnSite?: boolean;
  customerAgreesToPowerDown?: boolean;
  accessInstructions?: string;
  parkingInfo?: string[];
  whatThreeWords?: string;
  meterAccessible: boolean;
  extraSkills?: string[];
  ramsRequired: boolean;
  ramsEmail?: string;
  jobComments: string;
  isWarrantJob: boolean;
  smsConfirmationRequired?: boolean;
  smsReminderRequired?: boolean;
  smsConfirmationNumber?: string;
  relatedBookingId?: string;
  warrantOfficerName?: string;
  warrantOfficerContactNumber?: string;
  siteContactDetails: StateContactDetails;
  siteAddress: StateAddressDetails;
  dnoBnoSiteInfo?: DnoBnoSiteInfo;
};

export interface JbpGeneralState {
  contractId?: string;
  contractCustomerName?: string;
  contractDisplayName?: string;
  userAssociatedMpids?: string[];
  site?: SiteDetails;
  offer?: OfferDetails;
  jobDetails?: JobDetails;
}

const initialState: JbpGeneralState = {};

export const jbpSlice = createSlice({
  name: 'jbp',
  initialState,
  reducers: {
    setUserAssociatedMpids: (state, action: PayloadAction<string[]>) => {
      state.userAssociatedMpids = action.payload;
    },
    setContract: (state, action: PayloadAction<{ contractId: string; contractCustomerName: string; contractDisplayName: string }>) => {
      state.contractId = action.payload.contractId;
      state.contractCustomerName = action.payload.contractCustomerName;
      state.contractDisplayName = action.payload.contractDisplayName;
    },
    setSite: (state, action: PayloadAction<getSiteSuccessType & { serviceLine: EServiceLine }>) => {
      const isDNOBNO = ['DNO', 'BNO'].includes(action.payload.serviceLine);
      const siteData: SiteDetails = {
        isWCJob: !isDNOBNO ? action.payload.SiteTypeCode?.includes('WC') : false,
        userMpid: action.payload.MPID,
        offerDateRange: {
          startDate: action.payload.OfferDateRange?.StartDate,
          endDate: action.payload.OfferDateRange?.EndDate
        },
        bookable: action.payload.Bookable,
        siteAddress: {
          addressName: action.payload.SiteAddress?.AddressName ?? '',
          addressLine1: action.payload.SiteAddress?.AddressLine1 ?? '',
          addressLine2: action.payload.SiteAddress?.AddressLine2 ?? '',
          addressLine3: action.payload.SiteAddress?.AddressLine3 ?? '',
          town: action.payload.SiteAddress?.Town ?? '',
          county: action.payload.SiteAddress?.County ?? '',
          postCode: action.payload.SiteAddress?.PostCode ?? ''
        },
        siteContactDetails: {
          title: action.payload.SiteContactDetails?.Title ?? '',
          contactFirstName: action.payload.SiteContactDetails?.ContactFirstName ?? '',
          contactSurname: action.payload.SiteContactDetails?.ContactSurname ?? '',
          emailAddress: action.payload.SiteContactDetails?.EmailAddress ?? '',
          contactNumber: action.payload.SiteContactDetails?.ContactNumber ?? ''
        },
        siteType: isDNOBNO ? action.payload.serviceLine : action.payload.SiteTypeCode
      };

      if (isDNOBNO) {
        siteData.updatedSiteType = action.payload.serviceLine;
      }

      state.site = siteData;
    },
    updateSiteTypeAndIsWCJob: (state, action: PayloadAction<{ updatedSiteType: string; isWCJob: boolean; bookingReason?: number }>) => {
      if (!state.site) return;
      state.site.updatedSiteType = action.payload.updatedSiteType;
      state.site.isWCJob = action.payload.isWCJob;
      state.site.bookingReason = action.payload.bookingReason;
    },
    handleWindowBooking(state, action: PayloadAction<{ windowBookingStartDate: string; force: boolean }>) {
      state.offer = {
        windowBookingStartDate: action.payload.windowBookingStartDate,
        offerTokenSelected: true,
        forceOfferSelection: action.payload.force
      };
    },
    setSelectedOffer(state, action: PayloadAction<{ offer: Offer; force: boolean } | undefined>) {
      if (!action.payload) {
        state.offer = undefined;
        return;
      }
      const { offer: payloadOffer, force } = action.payload;
      const offer = {
        scheduledDate: payloadOffer.ScheduledDate,
        offerToken: payloadOffer.OfferToken,
        spJobCode: payloadOffer.SpJobCode,
        dayOfWeek: payloadOffer.DayOfWeek,
        apptBand: payloadOffer.ApptBand,
        apptDuration: payloadOffer.ApptDuration,
        startTime: payloadOffer.StartTime,
        endTime: payloadOffer.EndTime
      };
      state.offer = {
        selectedOffer: offer,
        offerTokenSelected: true,
        forceOfferSelection: force
      };
    },
    setJobDetails(state, action: PayloadAction<JobDetails>) {
      state.jobDetails = action.payload;
    },
    clearJobDetails(state) {
      console.log('Clearing job details from state');
      state.jobDetails = undefined;
    }
  }
});

export const { setContract, setSite, updateSiteTypeAndIsWCJob, handleWindowBooking, setSelectedOffer, setUserAssociatedMpids, setJobDetails, clearJobDetails } = jbpSlice.actions;
export default jbpSlice.reducer;
